import React from 'react';
import { TrendingUp, Users, DollarSign, Award, Briefcase, Calendar, Target, CheckCircle } from 'lucide-react';

interface ExecutiveMetrics {
  years_experience?: number;
  team_size?: string;
  industries?: string[];
  budget_managed?: string;
  cost_savings?: string;
  revenue_growth?: string;
  client_retention?: string;
  key_achievements?: string[];
}

interface ExecutiveSummaryProps {
  metrics: ExecutiveMetrics;
  userName?: string;
}

const ExecutiveSummary: React.FC<ExecutiveSummaryProps> = ({ metrics, userName = 'Professional' }) => {
  const metricCards = [
    {
      icon: <Calendar className="h-6 w-6" />,
      label: 'Experience',
      value: metrics.years_experience ? `${metrics.years_experience}+ Years` : 'N/A',
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-900',
      iconBg: 'bg-blue-100'
    },
    {
      icon: <Users className="h-6 w-6" />,
      label: 'Team Size',
      value: metrics.team_size || 'N/A',
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-900',
      iconBg: 'bg-green-100'
    },
    {
      icon: <DollarSign className="h-6 w-6" />,
      label: 'Budget Managed',
      value: metrics.budget_managed || 'N/A',
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-900',
      iconBg: 'bg-amber-100'
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      label: 'Revenue Growth',
      value: metrics.revenue_growth || metrics.cost_savings || 'N/A',
      color: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-900',
      iconBg: 'bg-purple-100'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-8 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Executive Career Dashboard</h1>
            <p className="text-blue-100 text-lg">{userName}</p>
          </div>
          <div className="hidden md:block">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
              <Target className="h-12 w-12" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricCards.map((card, index) => (
          <div
            key={index}
            className={`${card.bgColor} rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`${card.iconBg} p-3 rounded-lg ${card.textColor}`}>
                {card.icon}
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">{card.label}</h3>
            <p className={`text-2xl font-bold ${card.textColor}`}>{card.value}</p>
          </div>
        ))}
      </div>

      {metrics.industries && metrics.industries.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Briefcase className="h-5 w-5 text-gray-700" />
            <h3 className="text-lg font-semibold text-gray-900">Industry Experience</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {metrics.industries.map((industry, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-900 rounded-full text-sm font-medium border border-blue-200"
              >
                {industry}
              </span>
            ))}
          </div>
        </div>
      )}

      {metrics.key_achievements && metrics.key_achievements.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Award className="h-5 w-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-gray-900">Key Achievements</h3>
          </div>
          <div className="space-y-3">
            {metrics.key_achievements.map((achievement, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">{achievement}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {metrics.client_retention && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-800 mb-1">Client Retention Rate</p>
              <p className="text-3xl font-bold text-green-900">{metrics.client_retention}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutiveSummary;
